'use client';

import type { ReactNode } from 'react';
import { useState, useMemo, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { WISHLIST_GUEST_USER_COOKIE_KEY } from '../constants';
import { createSafeContext } from './createSafeContext';
import { createGuestCustomerKey, getWishListCustomerKeyCookie } from '../utils/wishlist';
import { useCallbackRef } from '@skin-seoul/utils';

interface WishlistCustomerKeyContextValue {
  customerKey: string | undefined;
  isKeyResolved: boolean;
  customerId: string | undefined;
  isCustomerIdResolved: boolean;
  setCustomerKey: (customerKey: string) => void;
  resolveGuestCustomerKey: () => string;
  clearCustomerKey: () => void;
}

const [WishlistCustomerKeyContextProvider, useWishlistCustomerKeyContext] =
  createSafeContext<WishlistCustomerKeyContextValue>('WishlistCustomerKey');

interface WishlistCustomerKeyProviderProps {
  children: ReactNode;
  /**
   * The signed-in account's key, resolved by the host app.
   *
   * Identity lives outside this package so the wishlist domain never reaches into the user domain,
   * and so each app can resolve it however it authenticates.
   */
  customerId?: string;
  /** False while the host app is still determining whether anyone is signed in. */
  isCustomerIdResolved?: boolean;
  getWishlistCustomerKey?: () =>
    | { wishlistGuestKey: string | undefined }
    | Promise<{ wishlistGuestKey: string | undefined }>;
  setWishlistCustomerKey?: (customerKey: string) => void | Promise<void>;
}

export const WishlistCustomerKeyProvider = ({
  children,
  customerId,
  isCustomerIdResolved = true,
  getWishlistCustomerKey: _getWishlistCustomerKey,
  setWishlistCustomerKey: _setWishlistCustomerKey,
}: WishlistCustomerKeyProviderProps) => {
  const getWishlistCustomerKey = useCallbackRef(_getWishlistCustomerKey ?? getWishListCustomerKeyCookie);
  const setWishlistCustomerKeyStorage = useCallbackRef(
    _setWishlistCustomerKey ??
      ((customerKey: string) => {
        Cookies.set(WISHLIST_GUEST_USER_COOKIE_KEY, customerKey);
      }),
  );

  const [wishlistKey, setWishlistKey] = useState<{
    customerKey?: string;
    isKeyResolved: boolean;
  }>({
    customerKey: undefined,
    isKeyResolved: false,
  });

  // Mirrors the key synchronously. React event handlers run to completion before the next one
  // starts, so two hearts clicked in the same tick both still see the pre-render state value;
  // only a ref lets the second click observe the key the first one just minted.
  const customerKeyRef = useRef<string | undefined>(undefined);

  const persistCustomerKey = useCallbackRef((customerKey: string) => {
    const writeToStorage = () => {
      try {
        return Promise.resolve(setWishlistCustomerKeyStorage(customerKey));
      } catch (error) {
        return Promise.reject(error);
      }
    };

    // The first write runs synchronously, so the key is stored before the add request leaves.
    // A dropped write orphans the guest's wishlist on the next app start, so it is retried once;
    // if that also fails the in-memory key still serves the current session.
    void writeToStorage()
      .catch(writeToStorage)
      .catch(() => undefined);
  });

  const setCustomerKey = useCallbackRef((customerKey: string) => {
    customerKeyRef.current = customerKey;
    setWishlistKey({
      customerKey,
      isKeyResolved: true,
    });
    persistCustomerKey(customerKey);
  });

  const resolveGuestCustomerKey = useCallbackRef(() => {
    if (customerKeyRef.current) {
      return customerKeyRef.current;
    }

    const guestCustomerKey = createGuestCustomerKey();
    setCustomerKey(guestCustomerKey);

    return guestCustomerKey;
  });

  const clearCustomerKey = useCallbackRef(() => {
    customerKeyRef.current = undefined;
    setWishlistKey({ customerKey: undefined, isKeyResolved: true });
  });

  const contextValue = useMemo(
    () => ({
      customerKey: wishlistKey.customerKey,
      isKeyResolved: wishlistKey.isKeyResolved,
      customerId,
      isCustomerIdResolved,
      setCustomerKey,
      resolveGuestCustomerKey,
      clearCustomerKey,
    }),
    [
      wishlistKey.customerKey,
      wishlistKey.isKeyResolved,
      customerId,
      isCustomerIdResolved,
      setCustomerKey,
      resolveGuestCustomerKey,
      clearCustomerKey,
    ],
  );

  useEffect(() => {
    let isSubscribed = true;

    const applyStoredKey = (storedCustomerKey: string | undefined) => {
      if (!isSubscribed) {
        return;
      }

      // A key written while storage was still loading (login, first add) wins over the stored value.
      setWishlistKey((previousKey) => {
        if (previousKey.isKeyResolved) {
          return previousKey;
        }

        customerKeyRef.current = storedCustomerKey;
        return { customerKey: storedCustomerKey, isKeyResolved: true };
      });
    };

    (async () => {
      try {
        const { wishlistGuestKey } = await getWishlistCustomerKey();
        applyStoredKey(wishlistGuestKey);
      } catch {
        // Unreadable storage must still resolve, otherwise every guest read and toggle stays
        // disabled for the lifetime of the provider.
        applyStoredKey(undefined);
      }
    })();

    return () => {
      isSubscribed = false;
    };
  }, [getWishlistCustomerKey]);

  return <WishlistCustomerKeyContextProvider value={contextValue}>{children}</WishlistCustomerKeyContextProvider>;
};

export const useWishlistCustomerKey = useWishlistCustomerKeyContext;
